import { TutorialStep, TutorialContext, TutorialRegistry, PageTutorialConfig } from './types';
import { publicHeaderSteps, publicPageConfigs } from './steps/publicSteps';
import { clientHeaderSteps, clientPageConfigs } from './steps/clientSteps';
import { prestataireHeaderSteps, prestatairePageConfigs } from './steps/prestataireSteps';
import { adminHeaderSteps, adminPageConfigs } from './steps/adminSteps';

export * from './types';

// Registry of all tutorials by context
const tutorialRegistries: Record<TutorialContext, TutorialRegistry> = {
  public: {
    headerSteps: publicHeaderSteps,
    pages: publicPageConfigs
  },
  client: {
    headerSteps: clientHeaderSteps,
    pages: clientPageConfigs
  },
  prestataire: {
    headerSteps: prestataireHeaderSteps,
    pages: prestatairePageConfigs
  },
  admin: {
    headerSteps: adminHeaderSteps,
    pages: adminPageConfigs
  }
};

/**
 * Find the matching page config for a given pathname
 */
export function findPageConfig(
  context: TutorialContext,
  pathname: string
): PageTutorialConfig | null {
  const registry = tutorialRegistries[context];
  if (!registry) return null;

  // Try exact match first
  let config = registry.pages.find(
    p => p.exactMatch && p.routePattern === pathname
  );

  // Then try prefix match
  if (!config) {
    config = registry.pages.find(
      p => !p.exactMatch && pathname.startsWith(p.routePattern)
    );
  }

  return config || null;
}

/**
 * Get the active tab value from a data attribute
 */
export function getActiveTabFromDOM(dataAttribute: string): string | undefined {
  const element = document.querySelector(`[${dataAttribute}]`);
  if (element) {
    return element.getAttribute(dataAttribute) || undefined;
  }
  return undefined;
}

/**
 * Get tutorial steps for a given context, pathname, and optional tab
 */
export function getTutorialSteps(
  context: TutorialContext,
  pathname: string,
  activeTab?: string
): TutorialStep[] {
  const registry = tutorialRegistries[context];
  if (!registry) return [];

  // First, check for page-specific config
  const pageConfig = findPageConfig(context, pathname);
  
  if (pageConfig) {
    // Get steps from page config
    const steps = typeof pageConfig.steps === 'function'
      ? pageConfig.steps(activeTab)
      : pageConfig.steps;
    
    // Return page-specific steps if available
    if (steps.length > 0) {
      return steps;
    }
  }

  // Fall back to header steps
  return registry.headerSteps;
}

/**
 * Get the tab data attribute for a given page
 */
export function getTabDataAttribute(
  context: TutorialContext,
  pathname: string
): string | undefined {
  const pageConfig = findPageConfig(context, pathname);
  return pageConfig?.tabDataAttribute;
}

/**
 * Check if a step is a sidebar step (for mobile filtering)
 */
export function isSidebarStep(step: TutorialStep): boolean {
  return step.target.includes('sidebar-section') ||
         step.target.includes('sidebar-client') ||
         step.target.includes('sidebar-presta') ||
         step.target.includes('sidebar-admin');
}

export { tutorialRegistries };
